import * as React from 'react'

import { colors } from '../utils/style'

type Props = {
  items: Array<{ src: string; label: string }>
  index: number
  onClose: () => void
  thumbnail?: string | null
}

export default function LightBox({ items, index, onClose, thumbnail }: Props) {
  const [isClosing, setIsClosing] = React.useState(false)
  const [imageLoaded, setImageLoaded] = React.useState(false)

  const currentItem = items[index]

  // Reset loading state when image source changes
  React.useEffect(() => {
    setImageLoaded(false)
  }, [currentItem.src])

  const handleClose = React.useCallback(() => {
    if (!isClosing) {
      setIsClosing(true)
    }
  }, [isClosing])

  // Unmount after exit animation completes (200ms)
  React.useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(onClose, 200)
      return () => clearTimeout(timer)
    }
  }, [isClosing, onClose])

  // Handle Escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleClose])

  const handleBackdropClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose()
      }
    },
    [handleClose],
  )

  return (
    <div className={`lightbox ${isClosing ? 'closing' : ''}`}>
      <div className="title">
        <div className="label">{currentItem.label}</div>
        <button type="button" className="close-button" onClick={handleClose}>
          <svg width="24" height="24">
            <use xlinkHref="#close" />
          </svg>
        </button>
      </div>

      <div className="image-container" onClick={handleBackdropClick}>
        {!imageLoaded && (
          <div className="loading-indicator">
            <div className="spinner" />
          </div>
        )}
        {!imageLoaded && thumbnail && (
          <img
            className="thumbnail-preview"
            src={thumbnail}
            alt=""
            aria-hidden="true"
          />
        )}
        <img
          className={`image ${imageLoaded ? 'loaded' : ''}`}
          src={currentItem.src}
          alt={currentItem.label}
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      <style jsx>{`
        @keyframes lightbox-backdrop-enter {
          from {
            opacity: 0;
          }
        }

        @keyframes lightbox-backdrop-exit {
          to {
            opacity: 0;
          }
        }

        @keyframes lightbox-content-enter {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
        }

        @keyframes lightbox-content-exit {
          to {
            opacity: 0;
            transform: scale(0.95);
          }
        }

        @keyframes spinner-rotate {
          to {
            transform: rotate(360deg);
          }
        }

        .lightbox {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 10000;
          background: rgba(0, 0, 0, 0.85);

          animation: lightbox-backdrop-enter 250ms ease-out both;
        }

        .lightbox.closing {
          animation: lightbox-backdrop-exit 200ms ease-out both;
        }

        .image-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;

          animation: lightbox-content-enter 250ms ease-out both;
        }

        .closing .image-container {
          animation: lightbox-content-exit 200ms ease-out both;
        }

        .loading-indicator {
          position: absolute;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2.5px solid rgba(255, 255, 255, 0.25);
          border-top-color: ${colors.white};
          animation: spinner-rotate 0.8s linear infinite;
        }

        .thumbnail-preview {
          position: absolute;
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;

          filter: blur(20px);
          transform: scale(1.08);
        }

        .image {
          position: relative;
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          opacity: 0;
        }

        .image.loaded {
          opacity: 1;
          transition: opacity 300ms ease-out;
        }

        .title {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 11000;

          display: flex;
          align-items: center;
          width: 100%;
          padding: 20px;

          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0) 100%
          );
        }

        .label {
          font-size: 24px;
          font-weight: bold;

          color: ${colors.white};
        }

        .close-button {
          display: inline-flex;
          padding: 0;
          margin-left: auto;
          border: none;

          background: transparent;
          color: ${colors.white};

          cursor: pointer;
        }

        @media (prefers-reduced-motion: reduce) {
          .lightbox {
            animation: none;
          }

          .lightbox.closing {
            animation: none;
            opacity: 0;
            transition: opacity 200ms ease-out;
          }

          .image-container {
            animation: none;
          }

          .closing .image-container {
            animation: none;
          }

          .thumbnail-preview {
            filter: none;
            transform: none;
          }

          .image.loaded {
            transition: none;
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}
